import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ComponentProps } from 'react';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { thinMaterial } from '@sorare/core/src/style/utils';

import { ContenderDrawerContent } from './ContenderDrawerContent/index.tsx';
import type {
  ContenderDrawerQuery,
  ContenderDrawerQueryVariables,
  ContenderDrawer_so5LeaderboardContender,
} from './__generated__/index.graphql.ts';

const LAZY_CONTENDER_DRAWER_QUERY = gql`
  query ContenderDrawerQuery($contender: String!) {
    so5 {
      so5LeaderboardContender(slug: $contender) {
        slug
        ...ContenderDrawerContent_so5LeaderboardContender
      }
    }
  }
  ${ContenderDrawerContent.fragments.so5LeaderboardContender}
` as TypedDocumentNode<ContenderDrawerQuery, ContenderDrawerQueryVariables>;

const DrawerContent = styled.div`
  height: 100%;
  background: var(--c-black);

  &.transparent {
    ${thinMaterial}
  }

  @media ${laptopAndAbove} {
    width: var(--width);
  }
`;

type Props = {
  open: boolean;
  onClose?: () => void;
  contender: ContenderDrawer_so5LeaderboardContender | string;
  width?: string;
  transparent?: boolean;
} & Pick<
  ComponentProps<typeof ContenderDrawerContent>,
  'tab' | 'onChangeTab' | 'onClickGame'
>;

export const ContenderDrawer = ({
  open,
  onClose,
  contender,
  width = '440px',
  transparent,
  ...contentProps
}: Props) => {
  const isLaptopAndAbove = useIsLaptop();

  const { data } = useQuery(LAZY_CONTENDER_DRAWER_QUERY, {
    variables: {
      contender: contender as string,
    },
    skip: typeof contender !== 'string',
  });

  const so5LeaderboardContender =
    typeof contender !== 'string'
      ? contender
      : data?.so5.so5LeaderboardContender;

  if (!so5LeaderboardContender) {
    return null;
  }

  return (
    <Drawer
      open={open}
      onBackdropClick={onClose}
      fullScreen={!isLaptopAndAbove}
    >
      <DrawerContent
        style={{ '--width': width } as CSSProperties}
        className={classNames({ transparent })}
      >
        <ContenderDrawerContent
          onClose={onClose}
          so5LeaderboardContender={so5LeaderboardContender}
          {...contentProps}
        />
      </DrawerContent>
    </Drawer>
  );
};

ContenderDrawer.fragments = {
  so5LeaderboardContender: gql`
    fragment ContenderDrawer_so5LeaderboardContender on So5LeaderboardContender {
      slug
      ...ContenderDrawerContent_so5LeaderboardContender
    }
    ${ContenderDrawerContent.fragments.so5LeaderboardContender}
  ` as TypedDocumentNode<ContenderDrawer_so5LeaderboardContender>,
};
